import { useEffect, useRef, useState } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchIsGlobalScopeEnrolmentEnabledFromServer from '@atlassian/jira-issue-navigator-global-scope-enrolment/src/services/global-scope-enrolment/fetch-is-global-scope-enrolment-enabled-from-server/index.tsx';
import { getIsGlobalScopeEnrolmentEnabledFromLocalStorage } from '@atlassian/jira-issue-navigator-global-scope-enrolment/src/services/global-scope-enrolment/local-storage/index.tsx';
import type { CloudId } from '@atlassian/jira-shared-types/src/general.tsx';

export const useNINGlobalScopeEnrolment = ({
	cloudId,
	isExpanded,
}: {
	cloudId: CloudId;
	/**
	 * Whether the extensible dialog is expanded or not
	 */
	isExpanded: boolean;
}): boolean => {
	const fetched = useRef(false);
	const [isNinGlobalScopeEnrolmentEnabled, setIsNinGlobalScopeEnrolmentEnabled] = useState(() => {
		/**
		 * Avoiding reading from local storage during SSR
		 */
		if (__SERVER__) {
			return true;
		}

		const localStorageData = getIsGlobalScopeEnrolmentEnabledFromLocalStorage();

		if (localStorageData !== null && localStorageData !== undefined) {
			return !!localStorageData;
		}

		/**
		 * Defaulting to true because NIN is already used by the majority of the users
		 */
		return true;
	});

	useEffect(() => {
		/**
		 * Avoiding fetching when:
		 * - is SSR
		 * - we already fetched jira.user.nin.global.scope.enrolment
		 * - extensible dialog is not expanded (to avoid unnecessary requests)
		 */
		if (__SERVER__ || fetched.current || !isExpanded) {
			return;
		}

		fetchIsGlobalScopeEnrolmentEnabledFromServer(cloudId)
			.then((isNinGlobalScopeEnrolmentEnabledData) => {
				fetched.current = true;
				setIsNinGlobalScopeEnrolmentEnabled(isNinGlobalScopeEnrolmentEnabledData);
			})
			.catch((error) => {
				fireErrorAnalytics({
					meta: {
						id: 'useNINGlobalScopeEnrolment',
						packageName: 'jiraAtlassianNavigation',
						teamName: 'navigation',
					},
					error:
						error instanceof Error
							? error
							: new Error('Error fetching jira.user.nin.global.scope.enrolment from the server'),
					sendToPrivacyUnsafeSplunk: true,
				});
			});
	}, [cloudId, isExpanded]);

	return isNinGlobalScopeEnrolmentEnabled;
};
